import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Plus, Package } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * Combobox for picking a catalog article OR creating a free article.
 *
 * Props:
 *   value         — currently selected item_id (string) or null for free article
 *   freeName      — current free-article name to render when value is null
 *   items         — full catalog list (filtered/active items)
 *   placeholder   — default placeholder
 *   disabled      — true to lock the control
 *   onSelectItem  — (item) => void  fired when the user picks a catalog article
 *   onSelectFree  — (typedName) => void  fired when the user clicks
 *                   "+ Crea articolo libero «X»"; the parent should then open
 *                   the FreeArticleDialog and persist via POST /lines.
 *   testid        — base data-testid for tests
 */
export function ItemCombobox({
  value,
  freeName,
  items,
  placeholder = 'Seleziona articolo',
  disabled = false,
  onSelectItem,
  onSelectFree,
  testid = 'item-combobox',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedItem = useMemo(
    () => items.find((it) => it.id === value),
    [items, value]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (it.name || '').toLowerCase().includes(q));
  }, [items, search]);

  // Visible label on the trigger
  const label = selectedItem?.name
    || freeName
    || placeholder;

  const trimmed = search.trim();
  const noExactMatch = trimmed && !filtered.some(
    (it) => (it.name || '').toLowerCase() === trimmed.toLowerCase()
  );

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`w-64 justify-between font-normal ${!selectedItem && !freeName ? 'text-slate-400' : ''}`}
          data-testid={`${testid}-trigger`}
        >
          <span className="truncate flex items-center gap-2">
            {freeName && !selectedItem ? (
              <span className="text-amber-700 italic">{freeName}</span>
            ) : (
              <>{label}</>
            )}
          </span>
          <ChevronsUpDown size={14} className="opacity-50 flex-shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-2 border-b">
          <Input
            autoFocus
            placeholder="Cerca o scrivi un nuovo articolo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9"
            data-testid={`${testid}-search`}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 && !trimmed && (
            <div className="px-3 py-6 text-sm text-slate-400 text-center">Nessun articolo</div>
          )}
          {filtered.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => { onSelectItem?.(it); setOpen(false); setSearch(''); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"
              data-testid={`${testid}-option-${it.id}`}
            >
              <Package size={14} className="text-slate-400 flex-shrink-0" />
              <span className="flex-1 truncate">{it.name}</span>
              <span className="text-xs text-slate-400">{it.unit}</span>
              {value === it.id && <Check size={14} className="text-blue-600" />}
            </button>
          ))}
          {noExactMatch && onSelectFree && (
            <button
              type="button"
              onClick={() => { onSelectFree?.(trimmed); setOpen(false); setSearch(''); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-amber-50 text-left border-t bg-amber-50/50"
              data-testid={`${testid}-create-free`}
            >
              <Plus size={14} className="text-amber-600 flex-shrink-0" />
              <span className="flex-1 truncate">
                Crea articolo libero «<strong className="text-amber-700">{trimmed}</strong>»
              </span>
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
